import React from 'react';
import { Helmet } from 'react-helmet';
import Footer from './../../components/Footer';

const BalanceSheetVsPnL = () => {
  const title = "Bilanço ve Kâr-Zarar Tablosu: Farklar Nelerdir? | Smart Finans";
  const description = "Bilanço ve kâr-zarar tablosu arasındaki farkları öğrenin. Bu temel finansal tabloların detayları ve işletme performansını nasıl yansıttıkları hakkında bilgi edinin.";
  const currentYear = new Date().getFullYear();
  const canonicalUrl = "https://smart-portfoy.com/egitim/bilanco-kar-zarar-tablosu";


  const sections = [
    {
      title: 'Bilanço Nedir?',
      description: 'Bilanço, bir işletmenin belirli bir tarihteki mali durumunu gösteren bir tablodur. Varlıklar, yükümlülükler ve öz kaynakları kapsar.'
    },
    {
      title: 'Kâr-Zarar Tablosu Nedir?',
      description: 'Kâr-zarar tablosu, belirli bir dönemde işletmenin gelir ve giderlerini, dolayısıyla net kâr veya zararını gösterir.'
    },
    {
      title: 'Bilanço ve Kâr-Zarar Tablosu Arasındaki Farklar',
      points: [
        'Bilanço bir anı yansıtırken, kâr-zarar tablosu bir dönemi kapsar.',
        'Bilanço, varlıklar ve yükümlülükler dengesine odaklanırken, kâr-zarar tablosu gelir ve giderlere odaklanır.',
        'Bilanço öz sermaye analizinde kullanılır; kâr-zarar tablosu ise işletmenin performansını ölçer.'
      ]
    },
    {
      title: 'Bilanço ve Kâr-Zarar Tablosunun Bileşenleri',
      description: 'Her iki tablo da işletmenin mali durumunu ve performansını anlamak için kritik bilgiler sağlar.',
      points: [
        'Bilanço: Varlıklar, Yükümlülükler, Özkaynaklar',
        'Kâr-Zarar Tablosu: Gelirler, Giderler, Net Kâr/Zarar'
      ]
    },
    {
      title: 'Bilanço ve Kâr-Zarar Tablosu Nasıl Kullanılır?',
      description: 'Bu tablolar, yatırımcılar, analistler ve yöneticiler tarafından işletmenin mali sağlığını değerlendirmek için kullanılır.',
      points: [
        'Yatırımcılar: Şirketin mali sağlığını ve kârlılığını değerlendirir.',
        'Analistler: Şirketin performansını ve gelecekteki potansiyelini analiz eder.',
        'Yöneticiler: İşletme kararlarını desteklemek için mali verileri kullanır.'
      ]
    },
    {
      title: 'Smart Portfoy ile Finansal Analiz',
      description: 'Smart Portfoy ile finansal tabloları kolayca analiz edin, otomatik raporlar oluşturun ve şirket performansını daha iyi anlayın. '
    }
  ];

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="keywords" content="bilanço, kâr-zarar tablosu, finansal analiz, yatırım" />
      </Helmet>


      <div className="min-h-screen bg-gray-800 text-gray-100">
        {/* Hero Section */}
        <header className="bg-gradient-to-r from-green-900 to-teal-900 py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            <h1 className="text-4xl font-bold mb-4 mt-8 bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-teal-400 no-underline select-none">
              Bilanço ve Kâr-Zarar Tablosu: Farklar Nelerdir?
            </h1>
            <p className="text-lg text-gray-300 max-w-3xl leading-relaxed">
              Finansal tabloların temel farklarını anlayarak işletmenizin mali sağlığını daha iyi değerlendirin.
            </p>
          </div>
        </header>

        {/* Image Section with Gradient Overlay */}
        <div className="relative h-48"> {/* Reduced height */}
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/50 to-gray-900 z-10"></div>
          <img 
            src="/images/blog/rebalance/rebalance-guide.jpeg" 
            alt="Bilanço ve Kâr-Zarar Tablosu Karşılaştırması"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content Section */}
        <div className="container mx-auto px-4 max-w-6xl py-8">
          <div className="space-y-8">
            {sections.map((section, index) => (
              <section key={index} className="space-y-4">
                <h2 className="text-2xl font-bold mb-4 text-green-400 no-underline">
                  {section.title}
                </h2>
                {section.description && (
                  <p className="text-gray-300 text-base">{section.description}</p>
                )}
                {section.points && (
                  <ul className="space-y-2">
                    {section.points.map((point, pIndex) => (
                      <li key={pIndex} className="flex items-start space-x-2">
                        <span className="flex-shrink-0 w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center text-gray-900 font-bold">
                          {pIndex + 1}
                        </span>
                        <span className="text-gray-300">{point}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>
        </div>

        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Bilanço ve Kâr-Zarar Tablosu: Farklar Nelerdir?",
            "description": description,
            "author": {
              "@type": "Organization",
              "name": "Smart Finans"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Smart Finans"
            },
            "datePublished": `${currentYear}-01-25T00:00:00Z`,
            "dateModified": `${currentYear}-01-25T00:00:00Z`,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "/balance-sheet-vs-pnl"
            }
          })}
        </script>
      </div>

      <Footer />
    </>
  );
};

export default BalanceSheetVsPnL;